import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  X,
  Loader2,
  UserPlus,
  Phone,
  Mail,
  Trash2,
  ArrowRight,
  Flame,
  Clock,
  Target,
  Users,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { leads, staff } from '../api/endpoints.js'

const STATUSES = [
  ['new', 'New'],
  ['contacted', 'Contacted'],
  ['trial', 'On trial'],
  ['converted', 'Converted'],
  ['lost', 'Lost'],
]
const STATUS_LABEL = Object.fromEntries(STATUSES)

const STATUS_BADGE = {
  new: 'badge-info',
  contacted: 'badge-alert',
  trial: 'badge-info',
  converted: 'badge-active',
  lost: 'badge-cancelled',
}

const SOURCES = [
  ['website', 'Website'],
  ['walk_in', 'Walk-in'],
  ['referral', 'Referral'],
  ['social', 'Social'],
  ['event', 'Event'],
  ['other', 'Other'],
]
const SOURCE_LABEL = Object.fromEntries(SOURCES)

const EMPTY_LEAD = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  status: 'new',
  source: 'walk_in',
  assigned_to: '',
  notes: '',
}

// ─────────────────────────────────────────────────────────────────────────────

function LeadModal({ lead = null, staffList, onClose }) {
  const qc = useQueryClient()
  const isEdit = !!lead
  const [form, setForm] = useState(
    isEdit
      ? {
          ...EMPTY_LEAD,
          ...Object.fromEntries(
            Object.entries(lead).map(([k, v]) => [k, v ?? '']),
          ),
        }
      : EMPTY_LEAD,
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const change = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    if (!form.first_name.trim()) {
      setError('First name is required.')
      return
    }
    setSaving(true)
    setError('')
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      status: form.status,
      source: form.source,
      assigned_to: form.assigned_to || null,
      notes: form.notes.trim() || null,
    }
    try {
      if (isEdit) {
        await leads.update(lead.id, payload)
        toast.success('Lead updated')
      } else {
        await leads.create(payload)
        toast.success('Lead added')
      }
      qc.invalidateQueries({ queryKey: ['leads'] })
      onClose()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save lead.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 border border-surface-3 rounded-2xl w-full max-w-lg elev-2 animate-fade-in overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-surface-3 sticky top-0 bg-surface-2 z-10">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEdit ? 'Edit Lead' : 'New Lead'}
          </h2>
          <button onClick={onClose} className="text-text-dim hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {error && (
            <p className="text-sm text-status-suspended bg-status-suspended/10 border border-status-suspended/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">First name *</label>
              <input name="first_name" className="input-field" value={form.first_name}
                     onChange={change} required />
            </div>
            <div>
              <label className="label">Last name</label>
              <input name="last_name" className="input-field" value={form.last_name} onChange={change} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Email</label>
              <input name="email" type="email" className="input-field" value={form.email} onChange={change} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input name="phone" className="input-field" value={form.phone} onChange={change} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Status</label>
              <select name="status" className="input-field" value={form.status} onChange={change}>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Source</label>
              <select name="source" className="input-field" value={form.source} onChange={change}>
                {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Owner</label>
            <select name="assigned_to" className="input-field" value={form.assigned_to} onChange={change}>
              <option value="">Unassigned</option>
              {(staffList || []).map((s) => (
                <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea name="notes" rows={3} className="input-field resize-none"
                      value={form.notes} onChange={change}
                      placeholder="What are they after? When do they want to start?" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isEdit ? 'Save' : 'Add lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function ConvertModal({ lead, onClose }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [email, setEmail] = useState(lead.email || '')
  const [error, setError] = useState('')

  const convert = useMutation({
    mutationFn: () => leads.convert(lead.id, { email: email || null }).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['leads'] })
      qc.invalidateQueries({ queryKey: ['members'] })
      toast.success('Lead converted to member')
      onClose()
      navigate(`/members/${data.member_id}`)
    },
    onError: (err) => setError(err.response?.data?.detail || 'Conversion failed.'),
  })

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 border border-surface-3 rounded-2xl w-full max-w-md elev-2 animate-fade-in">
        <div className="flex items-center justify-between p-5 border-b border-surface-3">
          <h2 className="text-lg font-semibold text-text-primary">Convert to Member</h2>
          <button onClick={onClose} className="text-text-dim hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <p className="text-sm text-status-suspended bg-status-suspended/10 border border-status-suspended/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <p className="text-sm text-text-muted">
            Creates a member record for <strong className="text-text-primary">
              {lead.first_name} {lead.last_name}
            </strong> and marks this lead converted. You can enrol them in a plan
            on the member page.
          </p>
          <div>
            <label className="label">Email *</label>
            <input type="email" className="input-field" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   placeholder="required — members are keyed by email" />
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button onClick={() => convert.mutate()} disabled={!email || convert.isPending}
                    className="btn-primary flex-1">
              {convert.isPending
                ? <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                : <>Convert <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, tone = 'text-text-primary' }) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 text-xs text-text-dim uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
    </div>
  )
}

export default function LeadsPage() {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [converting, setConverting] = useState(null)

  const params = useMemo(() => {
    const p = {}
    if (q.trim()) p.q = q.trim()
    if (statusFilter) p.status = statusFilter
    if (sourceFilter) p.source = sourceFilter
    return p
  }, [q, statusFilter, sourceFilter])

  const { data: rows, isLoading } = useQuery({
    queryKey: ['leads', 'list', params],
    queryFn: () => leads.list(params).then((r) => r.data),
  })
  const { data: stats } = useQuery({
    queryKey: ['leads', 'stats'],
    queryFn: () => leads.stats().then((r) => r.data),
  })
  const { data: staffList } = useQuery({
    queryKey: ['staff', 'list'],
    queryFn: () => staff.list().then((r) => r.data),
  })

  const remove = useMutation({
    mutationFn: (id) => leads.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] })
      toast.success('Lead deleted')
    },
    onError: () => toast.error('Could not delete lead'),
  })

  const quickStatus = useMutation({
    mutationFn: ({ id, status }) => leads.update(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
    onError: () => toast.error('Could not update status'),
  })

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="text-sm text-text-muted mt-1">
            Every walk-in and enquiry, from first contact to signed member.
          </p>
        </div>
        <button onClick={() => setAdding(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> New lead
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Open leads"
                  value={(stats?.by_status?.new ?? 0) + (stats?.by_status?.contacted ?? 0) + (stats?.by_status?.trial ?? 0)} />
        <StatCard icon={Flame} label="New this week" value={stats?.new_last_7_days ?? 0} />
        <StatCard icon={Target} label="Conversion rate"
                  value={`${stats?.conversion_rate ?? 0}%`} tone="text-primary" />
        <StatCard icon={Clock} label="Going cold" value={stats?.stale ?? 0}
                  tone={(stats?.stale ?? 0) > 0 ? 'text-status-alert' : 'text-text-primary'} />
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim" />
          <input className="input-field pl-9" placeholder="Search name, email or phone…"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className="input-field w-auto" value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map(([v, l]) => (
            <option key={v} value={v}>{l}{stats?.by_status?.[v] != null ? ` (${stats.by_status[v]})` : ''}</option>
          ))}
        </select>
        <select className="input-field w-auto" value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}>
          <option value="">All sources</option>
          {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr>
              <th>Name</th><th>Contact</th><th>Status</th><th>Source</th>
              <th>Owner</th><th>Age</th><th />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-text-dim" />
              </td></tr>
            )}
            {!isLoading && (rows || []).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-text-dim">
                <UserPlus className="w-7 h-7 mx-auto mb-2 opacity-60" />
                No leads match this filter.
              </td></tr>
            )}
            {(rows || []).map((l) => (
              <tr key={l.id} className="table-row" onClick={() => setEditing(l)}>
                <td>
                  <span className="font-medium">{l.first_name} {l.last_name}</span>
                  {l.notes && (
                    <span className="block text-xs text-text-dim truncate max-w-[240px]">{l.notes}</span>
                  )}
                </td>
                <td className="text-text-muted">
                  {l.email && (
                    <span className="flex items-center gap-1.5 text-xs">
                      <Mail className="w-3 h-3" />{l.email}
                    </span>
                  )}
                  {l.phone && (
                    <span className="flex items-center gap-1.5 text-xs">
                      <Phone className="w-3 h-3" />{l.phone}
                    </span>
                  )}
                  {!l.email && !l.phone && <span className="text-text-dim">—</span>}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <select
                    className={`${STATUS_BADGE[l.status] || 'badge-info'} bg-transparent cursor-pointer`}
                    value={l.status || 'new'}
                    onChange={(e) => quickStatus.mutate({ id: l.id, status: e.target.value })}
                  >
                    {STATUSES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                  </select>
                </td>
                <td className="text-text-muted">{SOURCE_LABEL[l.source] || l.source}</td>
                <td className="text-text-muted">{l.assigned_to_name || 'Unassigned'}</td>
                <td className="text-text-muted">
                  {l.age_days === null || l.age_days === undefined ? '—' : `${l.age_days}d`}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1 justify-end">
                    {l.status !== 'converted' && (
                      <button className="btn-ghost px-2 py-1 rounded text-xs text-primary"
                              onClick={() => setConverting(l)}>
                        Convert
                      </button>
                    )}
                    <button
                      className="btn-ghost p-1.5 rounded text-text-dim hover:text-status-suspended"
                      onClick={() => {
                        if (confirm(`Delete lead ${l.first_name} ${l.last_name}?`)) remove.mutate(l.id)
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding && <LeadModal staffList={staffList} onClose={() => setAdding(false)} />}
      {editing && <LeadModal lead={editing} staffList={staffList} onClose={() => setEditing(null)} />}
      {converting && <ConvertModal lead={converting} onClose={() => setConverting(null)} />}
    </div>
  )
}
